import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2, Github, KeyRound, Loader2, Plus, XCircle, X,
} from "lucide-react";

import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { cn } from "@/lib/cn";
import { toastError, toastSuccess } from "@/lib/toast";
import {
  addProjectRepository, deleteProjectRepository, getProjectRepositories,
  setProjectRepositoryToken, setRepositoryToken, testProjectRepositoryAccess,
  testRepoAccess, updateProject, updateProjectRepository,
} from "@/api/projects";

// What a repository IS decides how it gets used, so this isn't cosmetic: only a
// page-serving repo's routes seed the crawler. A backend repo contributes its
// endpoints, models and permissions instead — otherwise the crawler spends its
// budget fetching /api/v1/... as if those were pages.
const KINDS = ["frontend", "backend", "mobile", "shared", "other"];

const IDLE = { status: "idle", message: "" };

/**
 * The Repository card: one repo for the whole product, or one per service.
 *
 * Single mode edits the project's own repository_url/token (unchanged from
 * before). Multi mode edits the project_repositories rows, each with its own
 * token and its own "Test access" — a dead backend token must never make a
 * working frontend repo look broken.
 */
export default function RepositoryConnections({ project, projectId, onSaved }) {
  const [mode, setMode] = useState(project?.repository_mode || "single");

  // --- single-repo state (the project's own columns) ------------------------
  const [repoUrl, setRepoUrl] = useState("");
  const [repoVisibility, setRepoVisibility] = useState("public");
  const [repoToken, setRepoToken] = useState("");
  const [repoSaving, setRepoSaving] = useState(false);
  const [repoTest, setRepoTest] = useState(IDLE);

  // --- multi-repo state (project_repositories) ------------------------------
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  // Per-row transient UI keyed by repo id: { token, testing, saving }.
  const [draft, setDraft] = useState({});
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setMode(project?.repository_mode || "single");
    setRepoUrl(project?.repository_url || "");
    // Visibility now comes from the stored row (loadRows below); this is only
    // the pre-load guess for the single-repo card.
    setRepoVisibility(project?.has_repository_token ? "private" : "public");
    setRepoToken("");
    setRepoTest(IDLE);
  }, [project?.id, project?.repository_url, project?.has_repository_token,
      project?.repository_mode]);

  const loadRows = useCallback(async () => {
    if (!projectId) return;
    setLoadingRows(true);
    try {
      const res = await getProjectRepositories(projectId);
      setRows(res.repositories || []);
    } catch (e) {
      toastError(e.message || "Could not load the connected repositories.");
    } finally {
      setLoadingRows(false);
    }
  }, [projectId]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const patchDraft = (id, patch) =>
    setDraft((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));

  // ---- single mode ---------------------------------------------------------
  const checkRepo = useCallback(async () => {
    if (!repoUrl.trim()) {
      setRepoTest({ status: "fail", message: "Enter a repository link first." });
      return;
    }
    setRepoTest({ status: "testing", message: "" });
    try {
      // Test against the SAVED project so a stored token is used when the field
      // is left blank — otherwise "Test access" would fail on a repo that works.
      const res = await testRepoAccess(projectId, {
        repositoryUrl: repoUrl.trim(),
        repositoryToken: repoToken.trim() || undefined,
      });
      setRepoTest({ status: res.ok ? "ok" : "fail", message: res.message || "" });
    } catch (e) {
      setRepoTest({ status: "fail", message: e.message || "Could not test access." });
    }
  }, [projectId, repoUrl, repoToken]);

  const saveRepo = useCallback(async () => {
    setRepoSaving(true);
    try {
      await updateProject(projectId, {
        repository_url: repoUrl.trim() || null,
        repository_mode: "single",
      });
      // The token is write-only and travels on its own endpoint — an empty
      // string clears a stored one rather than leaving a stale secret behind.
      if (repoVisibility === "private") {
        if (repoToken.trim()) await setRepositoryToken(projectId, repoToken.trim());
      } else {
        await setRepositoryToken(projectId, "");
      }
      setRepoToken("");
      await loadRows();
      onSaved?.();
      toastSuccess("Repository saved.");
    } catch (e) {
      toastError(e.message || "Could not save the repository.");
    } finally { setRepoSaving(false); }
  }, [projectId, repoUrl, repoVisibility, repoToken, loadRows, onSaved]);

  // ---- mode toggle ---------------------------------------------------------
  const switchMode = useCallback(async (next) => {
    setMode(next);
    try {
      await updateProject(projectId, { repository_mode: next });
      onSaved?.();
    } catch (e) {
      toastError(e.message || "Could not switch repository mode.");
    }
  }, [projectId, onSaved]);

  // ---- multi mode ----------------------------------------------------------
  const addRow = useCallback(async () => {
    setAdding(true);
    try {
      const row = await addProjectRepository(projectId, {
        repositoryUrl: "https://github.com/", kind: "other", isPrivate: true,
      });
      setRows((r) => [...r, row]);
      onSaved?.();
    } catch (e) {
      toastError(e.message || "Could not add the repository.");
    } finally { setAdding(false); }
  }, [projectId, onSaved]);

  const saveRow = useCallback(async (row) => {
    const pending = draft[row.id] || {};
    patchDraft(row.id, { saving: true });
    try {
      const fields = {};
      if (pending.repository_url !== undefined) fields.repository_url = pending.repository_url;
      if (pending.label !== undefined) fields.label = pending.label;
      if (pending.kind !== undefined) fields.kind = pending.kind;
      if (pending.is_private !== undefined) fields.is_private = pending.is_private;
      const updated = Object.keys(fields).length
        ? await updateProjectRepository(projectId, row.id, fields)
        : row;
      // The token only moves when the user typed one — a blank field means
      // "leave the stored token alone", never "delete it".
      if ((pending.token || "").trim()) {
        await setProjectRepositoryToken(projectId, row.id, pending.token.trim());
      }
      setRows((rs) => rs.map((r) => (r.id === row.id
        ? { ...updated, has_token: r.has_token || !!(pending.token || "").trim() }
        : r)));
      setDraft((d) => ({ ...d, [row.id]: {} }));
      onSaved?.();
      toastSuccess("Repository saved.");
    } catch (e) {
      toastError(e.message || "Could not save the repository.");
      patchDraft(row.id, { saving: false });
    }
  }, [projectId, draft, onSaved]);

  const testRow = useCallback(async (row) => {
    const pending = draft[row.id] || {};
    patchDraft(row.id, { testing: true });
    try {
      const res = await testProjectRepositoryAccess(projectId, row.id, {
        repositoryUrl: (pending.repository_url ?? row.repository_url) || undefined,
        repositoryToken: (pending.token || "").trim() || undefined,
      });
      // The verdict is stored server-side, so the row reflects it after a reload
      // too rather than resetting to "not tested yet".
      setRows((rs) => rs.map((r) => (r.id === row.id ? (res.repository || r) : r)));
    } catch (e) {
      setRows((rs) => rs.map((r) => (r.id === row.id
        ? { ...r, last_test_ok: false, last_test_message: e.message || "Could not test access." }
        : r)));
    } finally {
      patchDraft(row.id, { testing: false });
    }
  }, [projectId, draft]);

  const testAll = useCallback(async () => {
    // Sequential on purpose: each repo is a GitHub call and firing five at once
    // is the quickest way to get rate-limited on a shared token.
    for (const row of rows) await testRow(row);
  }, [rows, testRow]);

  const removeRow = useCallback(async (row) => {
    try {
      await deleteProjectRepository(projectId, row.id);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      onSaved?.();
    } catch (e) {
      toastError(e.message || "Could not remove the repository.");
    }
  }, [projectId, onSaved]);

  const connected = mode === "multi"
    ? rows.filter((r) => r.last_test_ok).length
    : (project?.repository_url ? 1 : 0);

  return (
    <div className="card overflow-hidden">
      <div className="border-b border-slate-200 px-5 py-4 dark:border-white/10">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
          <Github size={16} className="text-slate-500" /> Repository
          {connected > 0 && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
              {mode === "multi" ? `${connected} connected` : "connected"}
            </span>
          )}
        </h3>
        <p className="mt-0.5 text-xs text-slate-400">
          Optional. When connected we read the repo&apos;s routes and forms, so the
          generated cases match what the code actually does.
        </p>
      </div>

      <div className="space-y-4 px-5 py-4">
        {/* mode */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">
            How is the code organised?
          </label>
          <Segmented
            options={[
              { value: "single", label: "Mono repository" },
              { value: "multi", label: "Polyrepo" },
            ]}
            value={mode}
            onChange={switchMode}
          />
          <p className="mt-2 text-xs text-slate-400">
            {mode === "single"
              ? "One repository backs the whole product."
              : "Frontend, backend and anything else live in their own repos, each with its own access token."}
          </p>
        </div>

        {mode === "single" ? (
          <div className="space-y-4 border-t border-slate-200 pt-4 dark:border-white/10">
            <Input
              label="Repository link" icon={Github}
              placeholder="https://github.com/your-org/your-app"
              value={repoUrl}
              onChange={(e) => { setRepoUrl(e.target.value); setRepoTest(IDLE); }}
            />

            {repoUrl.trim() && (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">
                  Repository visibility
                </label>
                <Segmented
                  options={[
                    { value: "public", label: "Public" },
                    { value: "private", label: "Private" },
                  ]}
                  value={repoVisibility}
                  onChange={(v) => { setRepoVisibility(v); setRepoTest(IDLE); }}
                />
              </div>
            )}

            {repoUrl.trim() && repoVisibility === "private" && (
              <>
                <Input
                  label="Repository access token" icon={KeyRound} type="password"
                  placeholder={project?.has_repository_token
                    ? "A token is stored — type a new one to replace it"
                    : "github_pat_… (required for private repos)"}
                  value={repoToken}
                  onChange={(e) => { setRepoToken(e.target.value); setRepoTest(IDLE); }}
                />
                <p className="-mt-2 text-xs text-slate-400">
                  Stored encrypted and never shown again — we only report whether one exists.
                </p>
              </>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={saveRepo} disabled={repoSaving}>
                {repoSaving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save repository"}
              </Button>
              <Button variant="outline" size="sm" onClick={checkRepo}
                disabled={repoTest.status === "testing" || !repoUrl.trim()}>
                {repoTest.status === "testing" ? <><Loader2 size={14} className="animate-spin" /> Testing…</> : "Test access"}
              </Button>
              {repoTest.status === "ok" && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 size={14} /> {repoTest.message || "Reachable."}
                </span>
              )}
              {repoTest.status === "fail" && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">
                  <XCircle size={14} /> {repoTest.message}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 border-t border-slate-200 pt-4 dark:border-white/10">
            {loadingRows && !rows.length && (
              <p className="text-xs text-slate-400">Loading connected repositories…</p>
            )}
            {!loadingRows && !rows.length && (
              <p className="text-xs text-slate-400">
                No repositories connected yet. Add the frontend and backend repos —
                each keeps its own access token.
              </p>
            )}

            {rows.map((row) => (
              <RepoRow
                key={row.id}
                row={row}
                draft={draft[row.id] || {}}
                onChange={(patch) => patchDraft(row.id, patch)}
                onSave={() => saveRow(row)}
                onTest={() => testRow(row)}
                onRemove={() => removeRow(row)}
              />
            ))}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={addRow} disabled={adding}>
                {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Add repository
              </Button>
              {rows.length > 1 && (
                <Button variant="ghost" size="sm" onClick={testAll}>Test all</Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// One connected repository: name + kind, link, visibility, its own token, and
// its own Test access verdict.
function RepoRow({ row, draft, onChange, onSave, onTest, onRemove }) {
  const url = draft.repository_url ?? row.repository_url ?? "";
  const label = draft.label ?? row.label ?? "";
  const kind = draft.kind ?? row.kind ?? "other";
  const isPrivate = draft.is_private ?? row.is_private ?? false;
  const dirty = ["repository_url", "label", "kind", "is_private"]
    .some((k) => draft[k] !== undefined) || !!(draft.token || "").trim();

  // The stripe encodes the last verdict, so an unverified or broken repo is
  // obvious without reading the status line.
  const stripe = draft.testing ? "bg-amber-400"
    : row.last_test_ok === true ? "bg-emerald-500"
    : row.last_test_ok === false ? "bg-rose-500"
    : "bg-slate-200 dark:bg-white/10";

  return (
    <div className="relative rounded-xl border border-slate-200 bg-slate-50/60 p-4 dark:border-white/10 dark:bg-white/[0.03]">
      <span className={cn("absolute left-0 top-3 bottom-3 w-0.5 rounded-full", stripe)} />

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[160px] flex-1">
          <Input
            label="Name" placeholder="Frontend"
            value={label}
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </div>
        <div className="w-40">
          <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
            What it is
          </label>
          <select
            value={kind}
            onChange={(e) => onChange({ kind: e.target.value })}
            className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm capitalize text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white"
          >
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <button
          type="button" onClick={onRemove} aria-label={`Remove ${label || "repository"}`}
          className="mb-0.5 grid h-11 w-11 place-items-center rounded-xl border border-slate-300 text-slate-400 transition-colors hover:border-rose-300 hover:text-rose-500 dark:border-white/10"
        >
          <X size={14} />
        </button>
      </div>

      <div className="mt-3">
        <Input
          label="Repository link" icon={Github}
          placeholder="https://github.com/your-org/your-app"
          value={url}
          onChange={(e) => onChange({ repository_url: e.target.value })}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">
            Visibility
          </label>
          <Segmented
            options={[
              { value: "public", label: "Public" },
              { value: "private", label: "Private" },
            ]}
            value={isPrivate ? "private" : "public"}
            onChange={(v) => onChange({ is_private: v === "private" })}
          />
        </div>
        <div className="min-w-[220px] flex-1">
          <Input
            label="Access token" icon={KeyRound} type="password"
            disabled={!isPrivate}
            placeholder={!isPrivate
              ? "Not needed for a public repository"
              : row.has_token
                ? "A token is stored — type a new one to replace it"
                : "github_pat_… (required for private repos)"}
            value={draft.token || ""}
            onChange={(e) => onChange({ token: e.target.value })}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onSave} disabled={draft.saving || !dirty}>
          {draft.saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save"}
        </Button>
        <Button variant="outline" size="sm" onClick={onTest} disabled={draft.testing}>
          {draft.testing ? <><Loader2 size={14} className="animate-spin" /> Testing…</> : "Test access"}
        </Button>
        <RepoStatus row={row} testing={draft.testing} />
      </div>
    </div>
  );
}

function RepoStatus({ row, testing }) {
  if (testing) {
    return <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Checking access…</span>;
  }
  if (row.last_test_ok === true) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 size={14} /> {row.last_test_message || "Reachable."}
      </span>
    );
  }
  if (row.last_test_ok === false) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">
        <XCircle size={14} /> {row.last_test_message || "Could not read this repository."}
      </span>
    );
  }
  return <span className="text-xs text-slate-400">Not tested yet.</span>;
}

// Two-or-three-way segmented control, matching the one the Repository card
// already used for public/private.
function Segmented({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 dark:border-white/10 dark:bg-white/5">
      {options.map((o) => (
        <button
          key={o.value} type="button" onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={cn(
            "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
            value === o.value
              ? "bg-white text-slate-900 shadow-soft dark:bg-white/10 dark:text-white"
              : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200",
          )}
        >{o.label}</button>
      ))}
    </div>
  );
}
